import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ReportErrorBoundaryClass extends Component<Props & { navigate: (path: string) => void }, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Report Error:', error, errorInfo);
    toast.error('An error occurred while displaying the report');
  }

  private handleReturnToDashboard = () => {
    this.props.navigate('/dashboard');
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Unable to Display Report
            </h2>
            <p className="text-gray-600 mb-6">
              {this.state.error?.message || 'An unexpected error occurred while displaying your report.'}
            </p>
            <button
              onClick={this.handleReturnToDashboard}
              className="w-full bg-primary text-dark px-4 py-2 rounded-lg hover:bg-primary-hover transition-colors"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default function ReportErrorBoundary(props: Props) {
  const navigate = useNavigate();
  return <ReportErrorBoundaryClass {...props} navigate={navigate} />;
}